import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, SESSION_LOST_EVENT } from '~/lib/api-client';
import { authMeRetry, authMeRetryDelay, authGateView } from '~/lib/auth-gate';
import { markAppNavigation } from '~/lib/page-load-reason';

export interface AuthMeResponse {
  authenticated: boolean;
  userId?: string;
  expiresAt?: number;
  name?: string;
  email?: string;
  /** FRONT-145: kb:admin / kb:superadmin for the session's org. */
  isAdmin?: boolean;
  /** FRONT-2096: the Eddy seat. `false` = signed in, may open shared artifacts, nothing else. */
  eddyAccess?: boolean;
}

/**
 * Up-front auth gate. Without this, an unauthenticated visit kicks off a
 * dozen parallel API queries that all 401 — looking like a bug instead of a
 * "you need to log in" state. Bouncing to /login on the first authoritative
 * 401 from /api/auth/me is the cleanest signal: the global query handler
 * stays out of the redirect business (a single endpoint failing shouldn't
 * boot the user out), but the layout gate explicitly checks once and
 * redirects when it knows there's no session.
 */
export function useAuthGate(): { ready: boolean; authed: boolean; eddyAccess: boolean } {
  const queryClient = useQueryClient();
  // FRONT-561: latch when the api-client confirms the session is dead (a 401
  // whose silent refresh also failed). This is the case /me can't catch — the
  // cookie still parses so /me says authenticated, but the access token is
  // expired and unrefreshable, so every protected call 401s. Re-checking /me
  // just re-affirms "authenticated" and the app hangs; latching forces the
  // logged-out redirect below.
  const [sessionLost, setSessionLost] = useState(false);

  // FRONT-157: the api-client fires this when a request 401'd AND the silent
  // refresh failed — the session is definitively dead.
  useEffect(() => {
    const onSessionLost = () => {
      setSessionLost(true);
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    };
    window.addEventListener(SESSION_LOST_EVENT, onSessionLost);
    return () => window.removeEventListener(SESSION_LOST_EVENT, onSessionLost);
  }, [queryClient]);

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await api.get<AuthMeResponse>('/api/auth/me');
      } catch (err) {
        // 401 throws an ApiError with status 401. Treat it as
        // "definitively not logged in" rather than a transient failure.
        if ((err as { status?: number } | null)?.status === 401) {
          return { authenticated: false } as AuthMeResponse;
        }
        throw err;
      }
    },
    staleTime: 60_000,
    // FRONT-340: only a 401 is a definitive "not signed in". A 429/5xx/
    // network answer says nothing about the session (a rate-limited IP must
    // NOT bounce the user to login) — keep retrying with capped exponential
    // backoff until the server gives a real answer.
    retry: authMeRetry,
    retryDelay: authMeRetryDelay,
  });

  const view = authGateView({
    meAuthenticated: query.data?.authenticated,
    meEddyAccess: query.data?.eddyAccess,
    settled: query.isSuccess || query.isError,
    sessionLost,
  });

  // Redirect to login once we know there's no usable session — either /me said
  // unauthenticated, or the session was confirmed lost (FRONT-561). Either way
  // `view` is ready-and-not-authed, so we never sit on a loader forever.
  useEffect(() => {
    if (!view.ready || view.authed) return;
    if (window.location.pathname.startsWith('/login')) return;
    if (window.location.pathname.startsWith('/auth-error')) return;
    const returnTo = window.location.pathname + window.location.search;
    // FRONT-3269: the login page's boot reports this as a session-lost load.
    markAppNavigation('session-lost');
    window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  }, [view.ready, view.authed]);

  return view;
}
